/* eslint-disable react-hooks/exhaustive-deps */
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { X } from 'lucide-react';
import Scrollbars from 'react-custom-scrollbars-2';
import Details, { DetailsSchema } from './drawer-tabs/Details';
import Elements, { reqElements } from './drawer-tabs/Elements';
import Media from './drawer-tabs/Media';
import Settings from './drawer-tabs/settings/Settings';
import useOrganizations from '../../features/organizations/useOrganizations';
import { SettingsSchema } from './drawer-tabs/settings/Settings';
import Loading from './drawer-tabs/settings/components/Loading';
import {
  useAddContentMutation,
  useUpdateContentMutation,
} from '@/features/organizations/api/contentApi';
import {
  useAddSettingMutation,
  useDeleteContentSettingMutation,
  useFetchContentSettingsQuery,
  useUpdateContentSettingMutation,
} from '@/features/organizations/api/settingsApi';
import useSnackbar from '@/hooks/useSnackbar';
import { useAccessControl } from '@/features/access-control';
import { Content, NewSetting, Setting } from '@/types';

const INITIAL_TAB = 'details';

interface Props {
  open: boolean;
  selectedContent?: Content;
  onClose: () => void;
}

const ContentDrawer = ({ open, selectedContent, onClose }: Props) => {
  const [openSnackbar] = useSnackbar();
  const { organizationId } = useParams();
  const { hasAccess } = useAccessControl();
  const { currentOrganization } = useOrganizations();
  const [addSetting] = useAddSettingMutation();
  const [updateSetting] = useUpdateContentSettingMutation();
  const [addContent, addContentMutation] = useAddContentMutation();
  const [updateContent, updateContentMutation] = useUpdateContentMutation();
  const [deleteSetting, deleteSettingMutation] =
    useDeleteContentSettingMutation();
  const [activeTab, setActiveTab] = useState<Tabs>(INITIAL_TAB);

  const isNewContent = useMemo(() => !selectedContent?.id, [selectedContent]);

  const tabs: Record<
    string,
    {
      label: string;
      component: ReactElement;
      schema?: any;
    }
  > = {
    details: {
      label: 'Detalles',
      component: <Details />,
      schema: DetailsSchema,
    },
    elements: {
      label:
        currentOrganization?.industry === 'food-and-beverages'
          ? 'Ingredientes'
          : 'Elementos',
      component: <Elements />,
      schema: Yup.object().shape({
        elements: Yup.array().of(reqElements),
      }),
    },
    media: {
      label: 'Multimedia',
      component: <Media />,
    },
    settings: {
      label: 'Configuración',
      component: <Settings />,
      schema: SettingsSchema,
    },
  };

  type Tabs = keyof typeof tabs;

  const handleChange = (_event: React.SyntheticEvent, newValue: Tabs) => {
    setActiveTab(newValue);
  };

  const contentSettings = useFetchContentSettingsQuery({
    organizationId: organizationId ?? skipToken,
    contentId: selectedContent?.id ?? skipToken,
  });

  const handleClose = () => {
    setActiveTab(INITIAL_TAB);
    onClose();
  };

  const handleUpdateSettings = async (settings: Setting[]) => {
    try {
      const promiseList = settings.map(
        (setting: Setting & { softDelete?: boolean }) => {
          // check if marked for soft delete
          if (setting?.softDelete) {
            return deleteSetting({
              organizationId: organizationId as string,
              contentId: selectedContent?.id ?? '',
              settingId: setting.id,
            });
          } else if (setting?.id) {
            // check if needs to be updated
            return updateSetting({
              organizationId: organizationId as string,
              contentId: selectedContent?.id ?? '',
              payload: setting,
            });
          } else {
            // check if needs to be created
            return addSetting({
              organizationId: organizationId as string,
              contentId: selectedContent?.id ?? '',
              payload: setting as NewSetting,
            });
          }
        }
      );
      await Promise.all(promiseList)
        .then(() => openSnackbar('Configuraciones guardadas', 'success'))
        .catch(() =>
          openSnackbar('Error borrando las configuraciones', 'error')
        );
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = useCallback(
    async (values: Content) => {
      try {
        type Payload = {
          content: Content;
          contentId: string;
          organizationId: string;
        };

        const payload: Payload = {
          content: values,
          contentId: selectedContent?.id ?? '',
          organizationId: organizationId ?? '',
        };

        const isNewContent = !values?.id;

        if (isNewContent) {
          const response = await addContent({
            draft: values,
            organizationId: organizationId ?? '',
          });

          if (response?.error) {
            openSnackbar('Error al crear el contenido', 'error');
          } else {
            openSnackbar('Contenido Creado', 'success');
            handleClose();
          }
        } else {
          const response = updateContent(payload);
          await handleUpdateSettings((values?.settings ?? []) as Setting[]);

          if (response?.error) {
            openSnackbar('Error al actualizar el contenido', 'error');
          } else {
            openSnackbar('Contenido Actualizado', 'success');
            handleClose();
          }
        }
      } catch (error) {
        console.error(error);
      }
    },

    [organizationId, selectedContent?.id]
  );

  const initialValues = useMemo(() => {
    return {
      ...selectedContent,
      settings: contentSettings.data ?? [],
    };
  }, [selectedContent, contentSettings?.data]);

  if (contentSettings.isLoading) {
    return <Loading />;
  }

  return (
    <Stack>
      <Drawer open={open} onClose={handleClose}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            padding: 2,
            paddingBottom: 0,
          }}
        >
          <Typography variant="h2">
            {isNewContent ? 'Crear' : 'Editar'} Contenido
          </Typography>
          <Paper
            sx={{
              borderRadius: '50%',
            }}
          >
            <IconButton onClick={handleClose} color="primary">
              <X />
            </IconButton>
          </Paper>
        </Stack>

        <Tabs value={activeTab} onChange={handleChange} sx={{ paddingX: 2 }}>
          {Object.entries(tabs).map(([tabKey, tab]) =>
            !isNewContent || tabKey !== 'settings' ? (
              <Tab key={tabKey} label={tab.label} value={tabKey} />
            ) : null
          )}
        </Tabs>

        <Divider />

        <Stack padding={2} gap={2} flex={1}>
          <Formik
            enableReinitialize
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={tabs[activeTab].schema}
          >
            {({ isSubmitting }) => (
              <Form
                style={{
                  height: '100%',
                }}
              >
                <Stack
                  spacing={2}
                  sx={{
                    height: '100%',
                  }}
                >
                  <Scrollbars autoHide>
                    <Stack
                      flex={1}
                      spacing={2}
                      sx={{
                        overflowY: 'scroll',
                        scrollbarWidth: 'none',
                        paddingBottom: 2,
                      }}
                    >
                      {isNewContent && activeTab === 'settings'
                        ? null
                        : tabs[activeTab].component}
                    </Stack>
                  </Scrollbars>

                  <Stack
                    direction={'row'}
                    spacing={2}
                    justifyContent={'flex-start'}
                  >
                    {hasAccess(
                      isNewContent ? 'create.content' : 'update.content'
                    ) ? (
                      <LoadingButton
                        variant="contained"
                        color="success"
                        type="submit"
                        loading={
                          addContentMutation.isLoading ||
                          updateContentMutation.isLoading ||
                          deleteSettingMutation.isLoading
                        }
                      >
                        {isNewContent ? 'Crear' : 'Guardar'}
                      </LoadingButton>
                    ) : null}
                    <Button
                      variant="text"
                      color="primary"
                      onClick={handleClose}
                      disabled={isSubmitting}
                    >
                      Cancelar
                    </Button>
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </Drawer>
    </Stack>
  );
};

export default ContentDrawer;
